/**
 * «Səhifələr» bölməsinin sxemi: panelin hansı sahələri göstərəcəyi.
 *
 * Açarlar saytın i18n açarlarıdır. Panel yalnız DƏYİŞDİRİLMİŞ sahələri
 * serverə göndərir, sayt isə tapmadığı sahəni öz mətni ilə doldurur
 * (`client/src/hooks/usePageContent.ts`).
 *
 * ⚠️ `DEFAULTS` = saytın hazırkı mətnləridir və buraya `client/src/utils/i18n.ts`
 * faylından köçürülüb. Onlar YALNIZ panelə başlanğıc mətn göstərmək üçündür —
 * saytın nə göstərdiyini təyin etmirlər. Yəni i18n mətni dəyişsə və bura
 * köçürülməsə, ən pis halda panel bir az köhnə mətn göstərər, sayt isə düzgün
 * işləməyə davam edər.
 */

/**
 * `html`  — mətn redaktoru (şəkil, link, qalın…). ⚠️ Belə sahə serverdə də
 *           `HTML_FIELDS` siyahısında olmalıdır, yoxsa təmizlənmədən saxlanılar.
 * `image` — şəkil yükləmə. Dəyər `fieldsAz/En`-də yox, `assets`-də saxlanılır,
 *           çünki foto hər iki dildə eynidir.
 */
export type FieldType = 'text' | 'multiline' | 'paragraphs' | 'html' | 'image';

export interface FieldDef {
  /** Saytın i18n açarı (şəkillərdə: `assets` açarı) */
  key: string;
  /** Paneldə görünən ad */
  label: string;
  type: FieldType;
  hint?: string;
}

export interface SectionDef {
  title: string;
  /** Bölmənin başında göstərilən xəbərdarlıq */
  note?: string;
  fields: FieldDef[];
}

export interface PageSchema {
  /** Server açarı — `EDITABLE_PAGES` ilə eyni olmalıdır */
  key: string;
  title: string;
  /** Saytdakı ünvan — «Səhifəyə bax» keçidi üçün */
  path: string;
  sections: SectionDef[];
}

export const ABOUT_PAGE: PageSchema = {
  key: 'about',
  title: 'Haqqımızda',
  path: '/about',
  sections: [
    {
      title: 'Səhifə başlığı',
      fields: [{ key: 'about.title', label: 'Başlıq', type: 'text' }],
    },
    {
      title: 'İdeya müəllifi',
      fields: [
        {
          key: 'about.author.photo',
          label: 'Şəkil',
          type: 'image',
          hint: 'Kvadrat şəkil daha yaxşı görünür (portret, ən azı 400×400). Yükləməsəniz nişan ikonu qalır.',
        },
        { key: 'about.author.title', label: 'Nişan (kiçik etiket)', type: 'text' },
        { key: 'about.author.name', label: 'Ad və soyad', type: 'text' },
        { key: 'about.author.subtitle', label: 'Vəzifə', type: 'text' },
        {
          key: 'about.author.bio',
          label: 'Bioqrafiya',
          type: 'html',
          hint: 'Şəkil əlavə etmək üçün kursoru istədiyiniz yerə qoyun və alətlər panelindəki şəkil düyməsini basın.',
        },
      ],
    },
    {
      title: 'Konfrans və Sammitlər',
      note: 'Kartların sayı sabitdir (5). Şəkil yükləməsəniz baş hərflər / tribuna nişanı görünür.',
      fields: [
        { key: 'about.summit.title', label: 'Bölmə başlığı', type: 'text' },
        { key: 'about.summit.description', label: 'Bölmə açıqlaması', type: 'multiline' },

        { key: 'about.summit.people.maria.photo', label: '1-ci kart — şəkil', type: 'image' },
        { key: 'about.summit.people.maria.name', label: '1-ci kart — ad', type: 'text' },
        { key: 'about.summit.people.maria.role', label: '1-ci kart — açıqlama', type: 'multiline' },

        { key: 'about.summit.people.richard.photo', label: '2-ci kart — şəkil', type: 'image' },
        { key: 'about.summit.people.richard.name', label: '2-ci kart — ad', type: 'text' },
        { key: 'about.summit.people.richard.role', label: '2-ci kart — açıqlama', type: 'multiline' },

        { key: 'about.summit.people.spsa.photo', label: '3-cü kart — şəkil', type: 'image' },
        { key: 'about.summit.people.spsa.name', label: '3-cü kart — ad', type: 'text' },
        { key: 'about.summit.people.spsa.role', label: '3-cü kart — açıqlama', type: 'multiline' },

        { key: 'about.summit.people.greatPlains.photo', label: '4-cü kart — şəkil', type: 'image' },
        { key: 'about.summit.people.greatPlains.name', label: '4-cü kart — ad', type: 'text' },
        { key: 'about.summit.people.greatPlains.role', label: '4-cü kart — açıqlama', type: 'multiline' },

        { key: 'about.summit.people.wpsa.photo', label: '5-ci kart — şəkil', type: 'image' },
        { key: 'about.summit.people.wpsa.name', label: '5-ci kart — ad', type: 'text' },
        { key: 'about.summit.people.wpsa.role', label: '5-ci kart — açıqlama', type: 'multiline' },
      ],
    },
    {
      title: 'Dəyərlərimiz',
      note: '⚠️ Bu üç kartın mətni ANA SƏHİFƏDƏ də göstərilir — burada dəyişsəniz, orada da dəyişəcək.',
      fields: [
        { key: 'about.valuesTitle', label: 'Bölmə başlığı', type: 'text' },
        { key: 'whyChoose.education.title', label: '1-ci dəyər — başlıq', type: 'text' },
        { key: 'whyChoose.education.description', label: '1-ci dəyər — mətn', type: 'multiline' },
        { key: 'whyChoose.justice.title', label: '2-ci dəyər — başlıq', type: 'text' },
        { key: 'whyChoose.justice.description', label: '2-ci dəyər — mətn', type: 'multiline' },
        { key: 'whyChoose.tolerance.title', label: '3-cü dəyər — başlıq', type: 'text' },
        { key: 'whyChoose.tolerance.description', label: '3-cü dəyər — mətn', type: 'multiline' },
      ],
    },
    {
      title: 'Aşağıdakı çağırış bloku',
      fields: [
        { key: 'about.joinTitle', label: 'Başlıq', type: 'text' },
        { key: 'about.joinText', label: 'Mətn', type: 'multiline' },
        { key: 'about.getStarted', label: 'Düymənin yazısı', type: 'text' },
      ],
    },
  ],
};

/** Paneldə redaktə oluna bilən bütün səhifələr. */
export const PAGES: PageSchema[] = [ABOUT_PAGE];

/** Sxemdəki bütün sahələr (bölmələr açılmış halda). */
export function allFields(page: PageSchema): FieldDef[] {
  return page.sections.flatMap((section) => section.fields);
}

/**
 * MƏTN sahələrinin açarları — `fieldsAz`/`fieldsEn`-ə gedənlər.
 * Şəkillər buraya DAXİL DEYİL: onlar dildən asılı olmadığı üçün ayrıca
 * `assets` xəritəsində saxlanılır.
 */
export function textFieldKeys(page: PageSchema): string[] {
  return allFields(page)
    .filter((field) => field.type !== 'image')
    .map((field) => field.key);
}

/** ŞƏKİL sahələrinin açarları — `assets`-ə gedənlər. */
export function imageFieldKeys(page: PageSchema): string[] {
  return allFields(page)
    .filter((field) => field.type === 'image')
    .map((field) => field.key);
}

const AZ: Record<string, string> = {
  'about.title': 'Haqqımızda',

  'about.author.title': 'İdeya Müəllifi',
  'about.author.name': 'İsmayıl Axundov',
  'about.author.subtitle': 'Yaradıcı və Təsisçi',
  'about.author.bio':
    'Mən, İsmayıl Axundov, 4 yanvar 2002-ci ildə Bakı şəhərində anadan olmuşam. 2019-cu ildə Azərbaycan Əmək və Sosial Münasibətlər Akademiyasının “Sosial iş” fakültəsinə qəbul olmuşam. Marağım siyasi elmlərə olsa da, 2023-cü ildə bakalavr təhsilimi başa vurmuşam.\n\n2022-ci ildə “Summer Work and Travel” proqramı vasitəsilə ABŞ-ın Viskonsin ştatında 3 aylıq iş təcrübəsi qazanmışam. Bu müddətdə ingilis dilimi inkişaf etdirib, Amerika mədəniyyətini və amerikalıların düşüncə tərzini öyrənmişəm. Dünyagörüşümü genişləndirmək məqsədilə müxtəlif təcrübələr əldə etmiş, müxtəlif millətlərdən olan insanlarla ünsiyyət quraraq onların həyat tərzlərini anlamağa çalışmışam. Bu proses baxışlarımın zənginləşməsinə səbəb olub. Eyni zamanda, iş təcrübəsi liderlik bacarıqlarımı inkişaf etdirməyə və yeni iş mühitlərinə uyğunlaşmağa imkan verib.\n\n2023-cü ildə magistr səviyyəsində sevdiyim ixtisas üzrə təhsil almaq üçün Türkiyənin İstanbul şəhərində yerləşən İstanbul Kent Universitetinin ingilis dilində tədris olunan “Siyasi Elmlər və Beynəlxalq Münasibətlər” ixtisasına qəbul olmuşam. Təhsil müddətində Amerikalı professorumun rəhbərliyi altında İsrail-Fələstin münaqişəsi çərçivəsində “One-State or Two-State Solution” mövzusunu əhatə edən bir araşdırma yazısı hazırlamışam.\n\n2024-cü ildə “Summer Work and Travel” proqramı vasitəsilə ikinci dəfə Amerikaya getmək imkanı qazanmışam. Bu dəfə məqsədim yalnız işləmək və səyahət etmək deyil, həm də Amerika cəmiyyətinə daha dərindən inteqrasiya olmaq, fərqli mədəniyyətləri yaxından tanımaq və şəxsi inkişafımı bir pillə də irəli aparmaq idi. Əldə etdiyim təcrübə və bacarıqları gələcək karyeramda istifadə etməyi, qarşıma məqsəd qoymuşdum.\n\n2025-ci ildə “What is the contribution of political parties under restrictive political systems?” mövzusunda yazdığım dissertasiyanı uğurla müdafiə edərək siyasi elmlər üzrə magistr dərəcəsi əldə etmişəm.\n\n2025-ci ilin 14-16 iyun tarixlərində Arizona ştatının paytaxtı Finiks şəhərində, ABŞ-ın tanınmış qeyri-hökumət təşkilatlarından biri “RepresentUs” tərəfindən təşkil olunan “American Democracy Summit 2025” sammitində iştirak etmişəm. Sammit çərçivəsində demokratik institutların gücləndirilməsi, seçki islahatları, şəffaf idarəetmə və digər mövzularda keçirilən panel və seminarlar, eləcə də interaktiv viktorinalar (quizlər) mənim elmi maraqlarımla birbaşa uyğunluq təşkil edib. Tədbir müddətində müxtəlif qeyri-hökumət təşkilatlarının nümayəndələri, jurnalistlər və bir neçə professorla görüşərək fikir mübadiləsi aparmaq imkanı əldə etmişəm. Bu görüşlər elmi düşüncə qabiliyyətimin artmasına və mövzu üzrə biliklərimin daha da dərinləşməsinə kömək olub.\n\n2025-ci ilin sentyabr ayında elmi rəhbərim Amerikada mənə dərhal elmi işimi təqdim etməyim üçün 3 fərqli (Southern Political Science Association, Western Political Science Association, Midwest Political Science Association) konfranslarında iştirak etməli olduğumu tələb etdi. Mövzularımın keyfiyyətinə görə hər 3 konfransdan qəbulum gəldi. 2026-cı ilin yanvar ayında, Louisiana ştatının New-Orleans şəhərinə, martda University of Missouri, Kansas şəhərinə, aprel ayında isə California, San Diegoya yola düşdüm.\n\nYaxın gələcəkdə doktorantura təhsili alaraq akademik fəaliyyətimi davam etdirmək və siyasi elmlər sahəsində elmi araşdırmalar apararaq yeni nəzəri və praktiki nəticələr əldə etməyə töhfə vermək istəyirəm. Təhsil və elmi fəaliyyətin cəmiyyətin inkişafındakı əhəmiyyətini nəzərə alaraq, bu istiqamətdə töhfə vermək arzusu mənim üçün xüsusi önəm daşıyır.\n\nHazırda Amerikada yaşayıram və burada həm şəxsi, həm də akademik inkişafımı davam etdirirəm.',

  'about.summit.title': 'Konfrans və Sammitlər',
  'about.summit.description':
    '«American Democracy Summit 2025» sammitindəki görüşlər və 2026-cı ilin siyasi elm konfransları.',
  'about.summit.people.maria.name': 'Maria McFarland Sánchez-Moreno',
  'about.summit.people.maria.role':
    '«RepresentUs», Baş İcraçı Direktoru — American Democracy Summit 2025.',
  'about.summit.people.richard.name': 'Richard C. Barton',
  'about.summit.people.richard.role':
    'Syracuse Universitetində professor — American Democracy Summit 2025.',
  'about.summit.people.spsa.name': 'Cənub Siyasi Elm Assosiasiyası (SPSA) Konfransı',
  'about.summit.people.spsa.role': 'Nyu-Orleans, Luiziana (2026, Yanvar).',
  'about.summit.people.greatPlains.name':
    '28-ci illik «Great Plains» Siyasi Elm Assosiasiyası konfransı',
  'about.summit.people.greatPlains.role': 'Missuri Universiteti, Kansas şəhəri (2026, Mart).',
  'about.summit.people.wpsa.name': 'Qərb Siyasi Elm Assosiasiyası (WPSA) konfransı',
  'about.summit.people.wpsa.role': 'Sessiya sədri və spiker — California, San Dieqo (2026, Aprel).',

  'about.valuesTitle': 'Dəyərlərimiz',
  'whyChoose.education.title': 'Maarifləndirmə',
  'whyChoose.education.description':
    'Siyasət, cəmiyyətin inkişafı üçün vacib bir alət olsa da, çoxları üçün mürəkkəb və başa düşülməsi çətin ola bilər. Bu səbəbdən, məqsədimiz insanları siyasi mövzularda maarifləndirməkdir. Platformamız, siyasət haqqında aydın və sadə məlumatlar təqdim edərək, hər kəsin siyasi məsələlərə dair daha yaxşı və məlumatlı qərarlar verə bilməsini təmin etməyi hədəfləyir. Maarifləndirmə vasitəsilə cəmiyyətdə siyasi bilik səviyyəsinin artırılmasına töhfə vermək niyyətindəyik.',
  'whyChoose.justice.title': 'Ədalət və Bərabərlik',
  'whyChoose.justice.description':
    'Siyasi sistemin düzgün işləməsi hər kəsin bərabər hüquq və imkanlara sahib olması ilə mümkündür. Platformamız isə siyasətə dair məlumatları ədalətli və bərabər şəkildə təqdim edir, fərqli baxışlara hörmətlə yanaşır.',
  'whyChoose.tolerance.title': 'Tolerantlıq',
  'whyChoose.tolerance.description':
    'Siyasi müzakirələr çox vaxt müxtəlif fikirlərin qarşılaşdığı və münaqişələrin yarandığı sahədir. Lakin biz inanırıq ki, fərqli fikirlərə hörmətlə yanaşmaq və tolerantlıq göstərmək, hər bir demokratik cəmiyyətin təməlini təşkil edir. Saytımız, istifadəçilərimizin müxtəlif baxışlarını qəbul edir və bu müxtəliflikdən faydalanaraq hər kəsin öz fikrini sərbəst şəkildə ifadə etməsinə şərait yaradır.',

  'about.joinTitle': 'İcmamıza Qoşulun',
  'about.joinText': 'Tezliklə',
  'about.getStarted': 'Başla',
};

const EN: Record<string, string> = {
  'about.title': 'About Us',

  'about.author.title': 'Idea Author',
  'about.author.name': 'İsmayıl Axundov',
  'about.author.subtitle': 'Visionary & Founder',
  'about.author.bio':
    "I, Ismayil Akhundov, was born in Baku on 4 January 2002. In 2019 I was admitted to the \"Social Work\" faculty of the Azerbaijan Academy of Labour and Social Relations. Although my interest lay in political science, I completed my bachelor's degree in 2023.\n\nIn 2022, through the \"Summer Work and Travel\" programme, I gained three months of work experience in the state of Wisconsin, USA. During that time I improved my English and learned about American culture and the American way of thinking. Seeking to broaden my outlook, I gathered a range of experiences and, by talking with people of many nationalities, tried to understand their ways of life. That process enriched my views. The work experience also helped me develop leadership skills and adapt to new working environments.\n\nIn 2023, to study my chosen field at master's level, I was admitted to the English-language \"Political Science and International Relations\" programme at Istanbul Kent University in Istanbul, Turkey. During my studies, under the supervision of my American professor, I prepared a research paper covering the topic \"One-State or Two-State Solution\" within the framework of the Israeli-Palestinian conflict.\n\nIn 2024 I had the opportunity to go to America a second time through the \"Summer Work and Travel\" programme. This time my aim was not only to work and travel, but also to integrate more deeply into American society, to get to know different cultures closely and to take my personal development a step further. I had set myself the goal of using the experience and skills I gained in my future career.\n\nIn 2025 I successfully defended my dissertation on \"What is the contribution of political parties under restrictive political systems?\" and obtained a master's degree in political science.\n\nOn 14-16 June 2025, in Phoenix, the capital of Arizona, I took part in the \"American Democracy Summit 2025\" organised by RepresentUs, one of the best-known non-governmental organisations in the USA. The panels and seminars held at the summit on strengthening democratic institutions, electoral reform, transparent governance and other topics — as well as the interactive quizzes — aligned directly with my academic interests. During the event I was able to meet and exchange ideas with representatives of various non-governmental organisations, journalists and several professors. Those meetings helped sharpen my academic thinking and deepen my knowledge of the field.\n\nIn September 2025 my academic supervisor in America required me to present my research without delay at three different conferences (Southern Political Science Association, Western Political Science Association, Midwest Political Science Association). Thanks to the quality of my topics, I was accepted by all three. In January 2026 I travelled to New Orleans, Louisiana; in March to the University of Missouri in Kansas City; and in April to San Diego, California.\n\nIn the near future I want to continue my academic work through doctoral study and, by carrying out scholarly research in political science, to contribute new theoretical and practical findings. Given the importance of education and scholarship to the development of society, the wish to contribute in this direction carries particular weight for me.\n\nI currently live in America, where I continue both my personal and academic development.",

  'about.summit.title': 'Conferences and Summits',
  'about.summit.description':
    'Meetings at the American Democracy Summit 2025 and the 2026 political science conferences.',
  'about.summit.people.maria.name': 'Maria McFarland Sánchez-Moreno',
  'about.summit.people.maria.role':
    'Chief Executive Officer, RepresentUs — American Democracy Summit 2025.',
  'about.summit.people.richard.name': 'Richard C. Barton',
  'about.summit.people.richard.role':
    'Professor at Syracuse University — American Democracy Summit 2025.',
  'about.summit.people.spsa.name': 'Southern Political Science Association (SPSA) Conference',
  'about.summit.people.spsa.role': 'New Orleans, Louisiana (January 2026).',
  'about.summit.people.greatPlains.name':
    '28th annual Great Plains Political Science Association conference',
  'about.summit.people.greatPlains.role': 'University of Missouri, Kansas City (March 2026).',
  'about.summit.people.wpsa.name': 'Western Political Science Association (WPSA) conference',
  'about.summit.people.wpsa.role': 'Panel chair and speaker — San Diego, California (April 2026).',

  'about.valuesTitle': 'Our Values',
  'whyChoose.education.title': 'Education',
  'whyChoose.education.description':
    'Politics is a vital tool for the development of society, yet for many it can be complex and hard to grasp. That is why our aim is to educate people on political topics. By presenting clear, simple information about politics, our platform sets out to help everyone make better, better-informed decisions on political matters. Through education we intend to contribute to raising the level of political knowledge in society.',
  'whyChoose.justice.title': 'Justice and Equality',
  'whyChoose.justice.description':
    'A political system works properly only when everyone holds equal rights and opportunities. Our platform presents information about politics fairly and equally, and treats differing views with respect.',
  'whyChoose.tolerance.title': 'Tolerance',
  'whyChoose.tolerance.description':
    'Political debate is often a field where different opinions meet and conflicts arise. But we believe that treating differing opinions with respect and showing tolerance forms the foundation of every democratic society. Our site accepts the varied views of our users and, drawing on that diversity, creates the conditions for everyone to express their opinion freely.',

  'about.joinTitle': 'Join Our Community',
  'about.joinText': 'Coming soon',
  'about.getStarted': 'Get Started',
};

export const DEFAULTS: Record<'az' | 'en', Record<string, string>> = { az: AZ, en: EN };
